import { Navigate, Route, Routes, useLocation } from "react-router";
import React, { Suspense, lazy, useEffect } from "react";

import { ConditionalRoute } from "./routes/ConditionalRoute";
import { Container } from "@mui/material";
import { Footer } from "./components/Footer/Footer";
import { Loader } from "./components/Loader";
import { Navbar } from "./components/Home/Header/Nav";
import { OverdueBanner } from "./components/OverdueBanner/OverdueBanner";
import { PATHS } from "./routes/routes";
import { SubscriptionType } from "./api/services/user/userSubscriptionService";
import { captureGclidFromUrl } from "./utils/gclid";
import { customColors } from "./theme";
import dayjs from "dayjs";
import { useAuthContext } from "./context/AuthContext";
import { useGTM } from "./hooks/useGTM";
import { useIsAdminQuery } from "./hooks/user/useIsAdminQuery";
import { useSubscriptionContext } from "./context/SubscriptionContext";
import PromoBanner from "./components/PromoBanner";

// Lazy load route components
const Home = lazy(() => import("./components/Home/Home"));
const PricingPage = lazy(() => import("./components/Pricing/PricingPage"));
const CheckYourEmail = lazy(
  () => import("./components/AuthComponents/CheckYourEmail")
);
const TermsOfUse = lazy(() => import("./components/Legal/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./components/Legal/PrivacyPolicy"));
const PhotoPage = lazy(() => import("./components/Photos/PhotoPage/PhotoPage"));
const PaymentStatus = lazy(() => import("./components/Pricing/PaymentStatus"));
const MyPhotosPage = lazy(
  () => import("./components/Photos/MyPhotosPage/MyPhotosPage")
);
const Settings = lazy(() => import("./components/Settings/Settings"));
const Gallery = lazy(() => import("./components/Gallery/Gallery"));
const AdminConsole = lazy(
  () => import("./components/AdminConsole/AdminConsole")
);
const SignIn = lazy(() => import("./components/AuthComponents/SignIn"));
const UploadPhotoPage = lazy(
  () => import("./components/Photos/UploadPhotoPage")
);
const CheckoutPage = lazy(() => import("./components/Payment/CheckoutPage"));
const CheckoutStatusPage = lazy(
  () => import("./components/Payment/CheckoutStatusPage")
);

const App = () => {
  useGTM();

  useEffect(() => {
    document.body.style.paddingTop = "env(safe-area-inset-top)";
  }, []);

  useEffect(() => {
    captureGclidFromUrl(); // saves gclid if present in the URL
  }, []);

  const location = useLocation();
  const { isUserVerified, isAuthLoading } = useAuthContext();
  const { subscriptionResult } = useSubscriptionContext();
  const { isAdmin } = useIsAdminQuery();

  const { plan, canceled, next_payment_date } = subscriptionResult || {};
  const isValidUser =
    isUserVerified && plan !== SubscriptionType.FREE && !canceled;

  const isOverdue =
    isValidUser &&
    next_payment_date &&
    dayjs(next_payment_date).isBefore(dayjs());

  const HIDDEN_NAVBAR_PATHS = [
    PATHS.login,
    PATHS.signup,
    PATHS.checkout,
    PATHS.checkoutStatus,
  ];
  const hideNavbar = HIDDEN_NAVBAR_PATHS.includes(location.pathname);

  const hideFooter = HIDDEN_NAVBAR_PATHS.includes(location.pathname);

  if (isAuthLoading) {
    return <Loader fullScreen />;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: customColors.background,
      }}
    >
      {!hideNavbar && !isValidUser && <PromoBanner />}
      {!hideNavbar && <Navbar />}
      {!hideNavbar && isOverdue && <OverdueBanner />}
      <Container
        disableGutters
        maxWidth={false}
        sx={{ flex: 1, padding: 0, margin: 0 }}
      >
        <Suspense fallback={<Loader fullScreen={false} />}>
          <Routes>
            {/* 🟢 Public Routes */}
            <Route path={PATHS.home} element={<Home />} />
            <Route path={PATHS.pricing} element={<PricingPage />} />
            <Route path={PATHS.login} element={<SignIn mode="signin" />} />
            <Route path={PATHS.signup} element={<SignIn mode="signup" />} />
            <Route path={PATHS.checkYourEmail} element={<CheckYourEmail />} />
            <Route path={PATHS.termsOfUse} element={<TermsOfUse />} />
            <Route path={PATHS.privacyPolicy} element={<PrivacyPolicy />} />
            <Route path={PATHS.checkout} element={<CheckoutPage />} />
            <Route
              path={PATHS.checkoutStatus}
              element={<CheckoutStatusPage />}
            />
            <Route path={PATHS.photo} element={<PhotoPage />} />
            <Route
              path={`${PATHS.paymentStatus}/:status`}
              element={<PaymentStatus />}
            />
            <Route path={PATHS.gallery} element={<Gallery />} />

            {/* 🔒 Conditional Routes */}
            {/* Allow My Photos and upload only for logged in users with cancelled or paid subscription */}
            <Route
              element={
                <ConditionalRoute
                  condition={
                    isUserVerified &&
                    (!!subscriptionResult?.canceled ||
                      subscriptionResult?.plan !== SubscriptionType.FREE)
                  }
                />
              }
            >
              <Route path={PATHS.myPhotos} element={<MyPhotosPage />} />
              <Route path={PATHS.uploadPhoto} element={<UploadPhotoPage />} />
            </Route>

            {/* Allow Settings only for logged in users*/}
            <Route element={<ConditionalRoute condition={isUserVerified} />}>
              <Route path={PATHS.settings} element={<Settings />} />
            </Route>

            {/* Allow Admin console only for admin users*/}
            <Route element={<ConditionalRoute condition={isAdmin === true} />}>
              <Route path={PATHS.adminConsole} element={<AdminConsole />} />
            </Route>

            {/* 🔄 Catch-all: Redirect unknown routes to Home */}
            <Route path="*" element={<Navigate to={PATHS.home} />} />
          </Routes>
        </Suspense>
      </Container>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default App;
